import { FC, Fragment, useContext, useRef, useState } from 'react';
import { MoContext } from 'contexts/MoContext';
import { actionTargetsOrder, useOrdersAction } from '../Assistant/ordersActions';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faTimes, faUnlock } from '@fortawesome/free-solid-svg-icons';
import { FullSegment, MyOrder, OrderRow } from 'ts/shared/interfaces/MOResponse';
import { getStatus } from 'constants/mo';
import Button from 'components/ui/Button/Button';
import useModalClass from 'hooks/useModalClass';
import { LockPnrParams, lockPnr } from 'services/myOrders';
import { showError, showSuccess } from 'utils/utils';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import { createPortal } from 'react-dom';
import useDraggableModal from 'hooks/useDraggableModal';

interface MoLockPnrModalProps {
  orderRow: OrderRow;
  fullSegment: FullSegment;
  onSuccess: (res: MyOrder) => void;
  hideMenu?: boolean;
}

const MoLockPnrModal: FC<MoLockPnrModalProps> = ({ orderRow, onSuccess, fullSegment, hideMenu = false }) => {
  const [t] = useTranslation();
  const { activeOrder } = useContext(MoContext);
  const isActive =
    activeOrder?.fullSegment.orderSegId === fullSegment.orderSegId ||
    activeOrder?.orderRow.segmentId === orderRow.segmentId;
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const isLocked = fullSegment.suppPnr.includes('BPRLOCK:');

  const onClickLock = async () => {
    setOpenModal(false);
    setLoading(true);

    const params: LockPnrParams = {
      orderId: orderRow.orderId,
      segmentId: orderRow.segmentId,
      lockPnr: !isLocked,
    };

    const response = await lockPnr(params);
    if (response && !response?.hasErrors) {
      const isLockedText = isLocked ? t('mo.unlock_pnr_success') : t('mo.lock_pnr_success');
      showSuccess(isLockedText);
      onSuccess(response.myOrder);
    } else if (response?.hasErrors) {
      showError(response?.errorMessage);
    }
    setLoading(false);
  };

  useOrdersAction(
    (action) =>
      hideMenu &&
      action.action === 'lock-pnr' &&
      !!fullSegment.rooms.length &&
      actionTargetsOrder(
        action,
        {
          segmentId: orderRow.segmentId,
          altSegmentId: fullSegment.orderSegId,
          orderId: orderRow.orderId,
          altOrderId: fullSegment.orderId,
        },
        isActive,
      ),
    (action) => {
      if (action.confirm) {
        void onClickLock();
        return;
      }
      setOpenModal(true);
    },
    isActive && hideMenu,
  );

  return (
    <Fragment>
      {!hideMenu && !!fullSegment.rooms.length && !isLocked && (
        <div className="c-dropdown-row" onClick={() => setOpenModal(true)}>
          <div>
            <FontAwesomeIcon icon={faLock} className="mr-5" size="sm" />
            {t('mo.lock_pnr')}
          </div>
        </div>
      )}

      {!hideMenu && !!fullSegment.rooms.length && isLocked && (
        <div className="c-dropdown-row" onClick={() => setOpenModal(true)}>
          <div>
            <FontAwesomeIcon icon={faUnlock} className="mr-5" size="sm" />
            {t('mo.unlock_pnr')}
          </div>
        </div>
      )}

      {openModal &&
        createPortal(
          <div
            className="modal-overlay modal-overlay-top modal-maximizable" style={{ direction: 'ltr' }}
            ref={modalOverlayRef}
            onClick={(e) => e.target === e.currentTarget && setOpenModal(false)}
          >
            <div ref={modalRef} className={classList} style={modalStyle}>
              <div
                className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
                {...dragHandleProps}
              >
                <div>
                  <span className="title mr-10">
                    {t(isLocked ? 'mo.unlock_pnr' : 'mo.lock_pnr')}
                    <span className="mr-5">{' '}-{' '}Order: {orderRow.orderId}</span>
                    <span className="mr-5">Segment: {orderRow.segmentId}</span>
                    {!!orderRow.status && (
                      <span className={`mo-status ${getStatus(orderRow.status)}`}>
                        {orderRow.status}
                      </span>
                    )}
                  </span>

                </div>
                <div data-no-drag="true">
                  <ScreenshotSupport />
                  <MaximizeModal modalOverlayRef={modalOverlayRef} />
                  <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
                </div>
              </div>
              <div className="modal-content">
                <div className="">{t('mo.are_you_sure')}</div>
              </div>

              <div className="modal-footer">
                <Button
                  className="btn btn-danger ml-0 mr-a"
                  title={t('confirm')}
                  disabled={loading}
                  onClick={() => onClickLock()}
                />
                <Button
                  className="btn btn-default"
                  title={t('close')}
                  onClick={() => setOpenModal(false)}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </Fragment>
  );
};

export default MoLockPnrModal;
